import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import * as API from '@/api/client';
import { User, UserState } from '@/shared/types/user';
import { defaultStatus, loadingStatus, failedStatus, successStatus } from '@/util/apiStatuses';

export const loadUser = createAsyncThunk<User>('user/login', async (_, { rejectWithValue }) => {
  try {
    const response = await API.loadUser();
    return response as User;
  } catch (error) {
    return rejectWithValue({ code: error.code, message: error.message });
  }
});

// If the server pre-injected user data (via window.MOVEO_REDUX_STATE) without a
// loginStatus, treat the user as already authenticated.
const getInitialState = (): UserState => {
  const windowState = (window as Window & { MOVEO_REDUX_STATE?: { user?: Partial<UserState> } })
    .MOVEO_REDUX_STATE;
  const serverUserState = windowState?.user;

  if (serverUserState?.user && !serverUserState.loginStatus) {
    return { user: serverUserState.user, loginStatus: successStatus };
  }

  return { user: null, loginStatus: defaultStatus };
};

const initialState: UserState = getInitialState();

const slice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setIframeView: (state: UserState, payload) => {
      state.iframeView = payload.payload;
    },
    updateUser: (state: UserState, payload) => {
      state.user = payload.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadUser.pending, (state) => {
        state.loginStatus = loadingStatus;
      })
      .addCase(loadUser.fulfilled, (state, action) => {
        state.user = action.payload;
        state.loginStatus = successStatus;
      })
      .addCase(loadUser.rejected, (state, action) => {
        state.loginStatus = failedStatus(action.payload);
      });
  },
});

export const { setIframeView, updateUser } = slice.actions;
export default slice.reducer;
